import { useEffect, useState } from "react";
import {
  splitTextIntoParagraphs,
  getConsecutiveAssistantMessages,
} from "./chatAudioUtils";
import { getLanguageFromCode, base64ToFile } from "../library";
import {
  convertActivityTemplateChatTextToSpeechWithAI,
  convertSubjectDescriptionChatTextToSpeechWithAI,
} from "../requests/AIRequests";
import {
  createOrUpdateStorageFileFromActivityTemplateChatAndActivityTemplateChatItem,
  createOrUpdateStorageFileFromSubjectDescriptionAndSubjectDescriptionChatAndSubjectDescriptionChatItem,
} from "../requests/StorageRequests";
import { NotificationManager } from "../notifications/NotificationManager";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

export const useTTSConversion = ({
  audioState,
  dispatchAudio,
  chatMode,
  chatList,
  chatLoading,
  chatCanConvertAudio,
  audioItemList,
  setAudioItemList,
  audioUrlList,
  setAudioUrlList,
  stopAndCleanupAudio,
  discardRecording,
  apiParams,
  onError,
}) => {
  const {
    userInformation,
    userProfile,
    curriculumType,
    brandId,
    campusId,
    institutionId,
    chatActivityId,
    chatActivityTemplateId,
    chatActivityTemplateType,
    chatSubjectDescriptionId,
    chatVoiceType,
    chatVoiceLanguage,
    chatActType,
  } = apiParams;

  const [lastChatId, setLastChatId] = useState(null);

  // TTS orchestration effect
  useEffect(() => {
    if (!chatCanConvertAudio) return;
    if (chatMode === "TEXT") {
      if (
        audioItemList.length > 0 ||
        audioUrlList.length > 0 ||
        audioState.isPlaying ||
        audioState.isReady ||
        audioState.isConverting
      ) {
        setAudioItemList([]);
        setAudioUrlList([]);
      }
      setLastChatId(null);
      return;
    }

    const lastChat = chatList[chatList.length - 1];

    if (lastChat && lastChat.role === "ASSISTANT") {
      const consecutiveAssistantMessages =
        getConsecutiveAssistantMessages(chatList);

      // Skip if still streaming
      if (chatLoading) return;

      // Create a unique ID for this group of assistant messages
      const groupId = consecutiveAssistantMessages
        .map((chat) => chat.id)
        .join("-");

      if (groupId && groupId === lastChatId) {
        if (
          audioState.isWaitingForUserVoice ||
          audioState.isWaitingForVoiceConversion ||
          audioState.isWaitingForAssistantResponse ||
          audioState.isWaitingForAssistantVoice ||
          audioState.isPlaying ||
          audioState.isReady ||
          audioState.isConverting
        )
          return;
        stopAndCleanupAudio();
        discardRecording();
        dispatchAudio({ type: "SET_WAITING_FOR_USER_VOICE" });
      } else {
        // If lastChatId is null (just switched to AUDIO), mark existing messages as seen
        if (lastChatId === null) {
          setLastChatId(groupId);
          dispatchAudio({ type: "SET_WAITING_FOR_USER_VOICE" });
          return;
        }

        setLastChatId(groupId);

        // Combine all consecutive assistant messages, preserving paragraph breaks
        const combinedText = consecutiveAssistantMessages
          .map((chat) => chat.text)
          .filter((text) => text)
          .join("\n\n");

        if (!combinedText) return;

        const paragraphs = splitTextIntoParagraphs(combinedText);
        if (paragraphs.length === 0) return;

        dispatchAudio({ type: "SET_WAITING_FOR_ASSISTANT_VOICE" });

        const audioItems = paragraphs.map((paragraph) => ({
          audioText: paragraph,
          ...lastChat,
        }));
        setAudioItemList(audioItems);
        setAudioUrlList([]);
      }
    } else if (
      lastChat &&
      lastChat.role === "USER" &&
      !audioState.isWaitingForUserVoice &&
      !audioState.isWaitingForVoiceConversion &&
      !audioState.isWaitingForAssistantResponse &&
      !audioState.isWaitingForAssistantVoice
    ) {
      setAudioItemList([]);
      setAudioUrlList([]);
    } else if (
      !lastChat &&
      !audioState.isWaitingForUserVoice &&
      !audioState.isWaitingForVoiceConversion &&
      !audioState.isWaitingForAssistantResponse &&
      !audioState.isWaitingForAssistantVoice
    ) {
      dispatchAudio({ type: "SET_WAITING_FOR_USER_VOICE" });
    }
  }, [
    chatMode,
    chatList,
    chatLoading,
    chatCanConvertAudio,
    audioState.isWaitingForUserVoice,
    audioState.isWaitingForVoiceConversion,
    audioState.isWaitingForAssistantResponse,
    audioState.isWaitingForAssistantVoice,
    stopAndCleanupAudio,
    discardRecording,
    lastChatId,
  ]);

  // TTS conversion loop effect
  useEffect(() => {
    if (
      chatMode !== "AUDIO" ||
      audioItemList.length === 0 ||
      audioState.isConverting ||
      audioState.convertIndex >= audioItemList.length
    ) {
      return;
    }

    const convertTextToAudio = async (item) => {
      // Skip if audioText is null or empty
      if (!item.audioText || !item.audioText.trim()) {
        dispatchAudio({
          type: "END_CONVERSION",
          payload: { audioItemListLength: audioItemList.length },
        });
        return;
      }

      dispatchAudio({ type: "START_CONVERSION" });
      try {
        let activityTemplateChatId = -1;
        let activityTemplateChatItemId = -1;
        let subjectDescriptionChatId = -1;
        let subjectDescriptionChatItemId = -1;

        let data;
        let err;

        if (chatSubjectDescriptionId) {
          ({ data, err } =
            await convertSubjectDescriptionChatTextToSpeechWithAI({
              audioText: item.audioText,
              userId: userInformation.id,
              userProfileId: userProfile.id,
              brandId,
              campusId,
              institutionId,
              subjectDescriptionId: chatSubjectDescriptionId,
              subjectDescriptionChatId,
              subjectDescriptionChatItemId,
              language: getLanguageFromCode(chatVoiceLanguage),
              voiceType: chatVoiceType,
              actType: chatActType,
              personalityType: "TEACHER",
            }));
        } else {
          ({ data, err } =
            await convertActivityTemplateChatTextToSpeechWithAI({
              audioText: item.audioText,
              userId: userInformation.id,
              userProfileId: userProfile.id,
              brandId,
              campusId,
              institutionId,
              activityId: chatActivityId,
              activityTemplateId: chatActivityTemplateId,
              activityTemplateType: chatActivityTemplateType,
              activityTemplateChatId,
              activityTemplateChatItemId,
              language: getLanguageFromCode(chatVoiceLanguage),
              voiceType: chatVoiceType,
              actType: chatActType,
              personalityType: "TEACHER",
            }));
        }

        if (err) throw err;

        const file = base64ToFile(
          data.audioByteArray,
          "assistant.mp3",
          "audio/mp3",
        );
        const formData = new FormData();
        formData.append("file", file);
        formData.append("fileType", "AUDIO");
        formData.append("curriculumType", curriculumType);
        formData.append(
          "path",
          `/${userInformation.id}/${userProfile.id}/audios`,
        );
        formData.append("userId", userInformation.id);
        formData.append("userProfileId", userProfile.id);

        if (chatSubjectDescriptionId) {
          formData.append("subjectDescriptionId", chatSubjectDescriptionId);
          formData.append("subjectDescriptionChatId", String(subjectDescriptionChatId));
          formData.append(
            "subjectDescriptionChatItemId",
            String(subjectDescriptionChatItemId),
          );
        } else {
          formData.append("activityTemplateChatId", String(activityTemplateChatId));
          formData.append(
            "activityTemplateChatItemId",
            String(activityTemplateChatItemId),
          );
          formData.append("activityId", chatActivityId);
          formData.append("activityTemplateId", chatActivityTemplateId);
        }

        let uploadData;
        let uploadErr;

        if (chatSubjectDescriptionId) {
          ({ data: uploadData, err: uploadErr } =
            await createOrUpdateStorageFileFromSubjectDescriptionAndSubjectDescriptionChatAndSubjectDescriptionChatItem(
              formData,
            ));
        } else {
          ({ data: uploadData, err: uploadErr } =
            await createOrUpdateStorageFileFromActivityTemplateChatAndActivityTemplateChatItem(
              formData,
            ));
        }

        if (uploadErr) throw uploadErr;

        const audioUrl = `${connection.base.storage.url}/storage-files/user-profile/preview/file${uploadData.path}`;

        // Only append if audio items haven't been cleared (user interrupted)
        if (audioItemList.length > 0) {
          setAudioUrlList((prev) => [...prev, audioUrl]);
        }
      } catch (error) {
        console.error("Audio conversion failed:", error);
        NotificationManager.error(
          "Failed to convert text to speech. Switching to text mode.",
        );
        onError();
        return null;
      } finally {
        dispatchAudio({
          type: "END_CONVERSION",
          payload: { audioItemListLength: audioItemList.length },
        });
      }
    };

    convertTextToAudio(audioItemList[audioState.convertIndex]);
  }, [
    chatMode,
    audioItemList,
    audioState.isConverting,
    audioState.convertIndex,
    userInformation,
    userProfile,
    brandId,
    campusId,
    institutionId,
    chatActivityId,
    chatActivityTemplateId,
    chatActivityTemplateType,
    chatSubjectDescriptionId,
    chatVoiceType,
    chatVoiceLanguage,
    chatActType,
  ]);
};
